import {
  UnifiedTheme,
  createUnifiedTheme,
  defaultTypography,
  genPageTheme,
  themes,
  shapes,
} from '@backstage/theme';

const defaultLightTheme = themes.light.getTheme('v4');

// ZEISS Beyond color tokens from https://beyond.zeiss.com/document/8#/color/color-palette-light-theme-1
const brandColors = {
  backgroundPrimary: '#ffffff',
  background01: '#f8fafc',
  background02: '#f2f5f8',
  background03: '#ecf0f4',
  background04: '#e6ebf0',
  background05: '#dce3e9',
  backgroundInteractive: '#0072ef',
  backgroundHover: '#0052aa',
  backgroundFocus: '#00346c',
  backgroundSuccessStrong: '#1e8565',
  backgroundCautionStrong: '#986f00',
  backgroundErrorStrong: '#e71e1e',
  backgroundChipStrong: '#4e565f',
  backgroundChipSubtle: '#e6ebf0',
  borderStrong: '#0e0f11',
  borderSubtle: '#c2cdd6',
  borderSubtlest: '#e6ebf0',
  iconInteractive: '#0065d1',
  iconHover: '#0052aa',
  iconFocus: '#00346c',
  iconSelected: '#0065d1',
  iconStrong: '#32373e',
  textOnColor: '#ffffff',
  textStrong: '#32373e',
  textSubtle: '#606a76',
  textSuccess: '#166249',
  textCaution: '#725400',
  textError: '#b21212',
  textInfo: '#0052aa',
  textHover: '#0052aa',
  textFocus: '#00346c',
};

export const beyondTheme: UnifiedTheme = createUnifiedTheme({
  typography: {
    ...defaultTypography,
    fontFamily: '"ZEISS Text UI Web", Helvetica, Arial, sans-serif',
  },
  components: {
    BackstageSidebar: {
      styleOverrides: {
        drawer: {
          top: '120px',
          left: '16px',
          bottom: '16px',
        },
      },
    },
    BackstageSidebarPage: {
      styleOverrides: {
        root: {
          flex: 1,
          minHeight: 0,
          overflow: 'visible',
        },
      },
    },
    BackstageContent: {
    styleOverrides: {
        root: {
          paddingLeft: '0 !important',
          paddingRight: '0 !important',
        },
      },  
    },
    BackstageSidebarItem: {
      styleOverrides: {
        root: {
          minHeight: 52,
          fontSize: '14px',
          fontWeight: 500,
          color: brandColors.textStrong,
          backgroundColor: brandColors.backgroundPrimary,
          '&$selected': {
            backgroundColor: brandColors.background01,
          },
          '&$selected:hover': {
            backgroundColor: brandColors.background02,
            color: brandColors.textHover,
          },
          '&:hover': {
            backgroundColor: brandColors.background02,
            color: brandColors.backgroundHover,
          },
        },
        buttonItem: {
          '&:hover': {
            backgroundColor: brandColors.background02,
          },
        },
        label: {
          paddingTop: 2,
        },
        selected: {
          '&:hover': {
            borderColor: brandColors.textHover,
          },
        },
      },
    },
    BackstageSidebarDivider: {
      styleOverrides: {
        root: {
          background: brandColors.borderSubtlest,
          color: brandColors.borderSubtlest,
          borderColor: brandColors.borderSubtlest,
        },
      },
    },
    BackstagePage: {
      styleOverrides: {
        root: {
          margin: 0,
          padding: 0,
        },
      },
    },
    BackstageHeader: {
      styleOverrides: {
        header: {
          backgroundImage: 'none',
          boxShadow: 'none',
          padding: 0,
          paddingTop: 8,
        },
        title: {
          color: brandColors.textStrong,
          fontSize: '28px',
          fontWeight: 700,
          lineHeight: '28px',
        },
      },
    },
    BackstageHeaderTabs: {
      styleOverrides: {
        tabsWrapper: {
          paddingLeft: 0,
          marginTop: 16,
        },
      },
    },
    BackstageInfoCard: {
      styleOverrides: {
        header: {
          padding: 12,
        },
      },
    },
    BackstageFavoriteToggleIcon: {
      styleOverrides: {
        icon: {
          color: brandColors.iconInteractive,
        },
      },
    },
    BackstageItemCardHeader: {
      styleOverrides: {
        root: {
          backgroundImage: 'none !important',
          backgroundColor: brandColors.background02,
        },
      },
    },
    CatalogReactUserListPicker: {
      styleOverrides: {
        root: {
          padding: 12,
        },
        groupWrapper: {
          margin: 0,
          padding: 0,
        },
        title: {
          margin: 0,
          textTransform: 'none',
          fontSize: 14,
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: brandColors.background04,
          },
          '&.Mui-selected:hover': {
            backgroundColor: brandColors.background05,
          },
          '&.Mui-disabled': {
            opacity: '1 !important',
          },
        },
      },
    },  
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: brandColors.textStrong,
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          color: brandColors.textStrong,
          '&:hover': {
            color: brandColors.textHover,
          },
        },
        primary: {
          fontFamily: '"ZEISS Text UI Web", Helvetica, Arial, sans-serif',
          fontSize: '14px',
          fontWeight: 500,
          paddingTop: 4,
          color: 'inherit',
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: 'inherit',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: '"ZEISS Text", Helvetica, Arial, sans-serif',
          fontWeight: 500,
          fontSize: '16px',
          lineHeight: '24px',
          letterSpacing: '0.32px',
          textTransform: 'none',
          borderRadius: 3,
          boxShadow: 'none',
          minHeight: '40px',
          minWidth: 'fit-content',
          padding: '10px 24px',
          '&.Mui-focusVisible': {
            backgroundColor: brandColors.backgroundFocus,
            boxShadow: '0 0 0 3px rgba(0, 114, 239, 0.24)',
          },
        },
        label: {
          gap: 8,
        },
        contained: {
          minHeight: '40px',
          boxShadow: 'none',
          padding: '8px 24px',
          backgroundColor: brandColors.backgroundInteractive,
          color: brandColors.textOnColor,
          '&:hover': {
            backgroundColor: brandColors.backgroundHover,
            boxShadow: 'none',
          },
          '&:active': {
            backgroundColor: brandColors.backgroundFocus,
          },
          '&.Mui-disabled': {
            backgroundColor: 'rgba(0, 114, 239, 0.35)',
            color: brandColors.textOnColor,
          },
        },
        outlined: {
          color: brandColors.iconStrong,
          borderColor: brandColors.borderStrong,
        },
        outlinedPrimary: {
          color: brandColors.iconInteractive,
          borderColor: brandColors.backgroundInteractive,
          '&:hover': {
            backgroundColor: 'rgba(0, 114, 239, 0.06)',
            color: brandColors.backgroundInteractive,
            borderColor: brandColors.backgroundHover,
            boxShadow: 'none',
          },
          '&:focus-visible': {
            backgroundColor: 'rgba(0, 114, 239, 0.06)',
            color: brandColors.backgroundInteractive,
            borderColor: brandColors.backgroundFocus,
            boxShadow: '0 0 0 3px rgba(0, 114, 239, 0.24)',
          },
          '&:active': {
            backgroundColor: 'rgba(0, 114, 239, 0.12)',
            color: brandColors.backgroundInteractive,
            borderColor: brandColors.backgroundFocus,
          },
          '&.Mui-disabled': {
            color: 'rgba(0, 114, 239, 0.35)',
            borderColor: 'rgba(0, 114, 239, 0.35)',
            backgroundColor: 'transparent',
          },
        },
        text: {
          minHeight: '40px',
          padding: '8px 24px',
          color: brandColors.backgroundInteractive,
          '&:hover': {
            backgroundColor: 'rgba(0, 114, 239, 0.06)',
            color: brandColors.backgroundInteractive,
            textDecoration: 'none',
          },
          '&:focus-visible': {
            backgroundColor: 'rgba(0, 114, 239, 0.06)',
            color: brandColors.backgroundInteractive,
            boxShadow: '0 0 0 3px rgba(0, 114, 239, 0.24)',
          },
          '&.Mui-disabled': {
            color: 'rgba(0, 114, 239, 0.35)',
          },
        },
        sizeLarge: {
          minHeight: '48px',
          padding: '12px 32px',
          '& .MuiSvgIcon-root, & .MuiButton-startIcon > *:first-child, & .MuiButton-endIcon > *:first-child': {
            fontSize: '20px',
          },
        },
        sizeSmall: {
          minHeight: '32px',
          padding: '6px 16px',
          fontSize: '14px',
          lineHeight: '20px',
          letterSpacing: '0.28px',
          '& .MuiSvgIcon-root, & .MuiButton-startIcon > *:first-child, & .MuiButton-endIcon > *:first-child': {
            fontSize: '12px',
          },
        },
        startIcon: {
          marginLeft: 0,
          marginRight: 0,
        },
        endIcon: {
          marginLeft: 0,
          marginRight: 0,
        },
      } as any,
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          '&:hover': {
            color: brandColors.iconHover,
          },
          '&:focus-visible': {
            color: brandColors.iconInteractive,
            boxShadow: '0 0 0 3px rgba(0, 114, 239, 0.24)',
          },
          '&.Mui-disabled': {
            color: 'rgba(0, 114, 239, 0.4)',
          },
        },
        colorInherit: {
          color: brandColors.iconInteractive,
        },
        colorSecondary: {
          color: brandColors.iconStrong,
        },
      } as any,
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: brandColors.backgroundPrimary,
          borderRadius: 8,
          boxShadow: 'none',
        },
        rounded: {
          borderRadius: 8,
        },
        elevation1: {
          boxShadow: 'none',
        },
        elevation2: {
          boxShadow: 'none',
        },
        elevation3: {
          boxShadow: 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: 'var(--global-background-primary) !important',
          borderRadius: 8,
          boxShadow: 'none',
          border: 'none',
          padding: 24,
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: '0 !important',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '12px 0px',
          height: 'inherit',
        },
      },
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          padding: '0px',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: brandColors.backgroundChipSubtle,
          color: brandColors.textStrong,
          height: '18px',
          paddingTop: '3px',
          marginRight: '2px !important',
          marginBottom: '2px !important',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: '36px',
          maxWidth: 'auto',
          minWidth: 'fit-content !important',
          padding: '0 !important',
          marginRight: 16,
          marginBottom: -2,
          borderBottom: `2px solid ${brandColors.borderSubtle}`,
          opacity: 1,
          background: 'none',
          '&:hover': {
            color: brandColors.backgroundHover,
            borderBottom: `2px solid ${brandColors.backgroundHover}`,
            backgroundColor: 'transparent',
            textDecoration: 'none',
          },
          '&.Mui-focusVisible': {
            color: brandColors.textFocus,
            borderBottomColor: brandColors.backgroundFocus,
            backgroundColor: 'transparent',
          },
          '&.Mui-selected': {
            color: brandColors.textStrong,
            borderBottomColor: brandColors.textStrong,
            opacity: 1,
            background: 'none',
          },
          '&.Mui-selected:hover': {
            color: brandColors.backgroundHover,
            borderBottom: `2px solid ${brandColors.backgroundHover}`,
            background: 'none',
          },
          '&.Mui-disabled': {
            color: 'rgba(96, 106, 118, 0.4)',
            borderBottomColor: brandColors.borderSubtlest,
            opacity: 0.4,
          },
        },
        wrapper: {
          color: brandColors.textStrong,
          textTransform: 'none',
          fontSize: 16,
          fontWeight: 700,
          '&:hover': {
            color: brandColors.backgroundHover,
          },
          '&.Mui-selected': {
            color: brandColors.textStrong,
            opacity: 1,
          },
          '&.Mui-disabled': {
            color: 'rgba(96, 106, 118, 0.4)',
            opacity: 0.4,
          },
        },
        textColorInherit: {
          opacity: 1,
          color: brandColors.textStrong,
        },
      } as any,
    },
    MuiTypography: {
      styleOverrides: {
        h5: {
          fontSize: '20px',
        },
      },
    },
  },
  palette: {
    ...defaultLightTheme?.palette,
    primary: {
      main: brandColors.backgroundInteractive,
    },
    secondary: {
      main: brandColors.textStrong,
    },
    error: {
      main: brandColors.backgroundErrorStrong,
      dark: brandColors.textError,
    },
    warning: {
      main: brandColors.backgroundCautionStrong,
      dark: brandColors.textCaution,
    },
    success: {
      main: brandColors.backgroundSuccessStrong,
      dark: brandColors.textSuccess,
    },
    info: {
      main: brandColors.backgroundInteractive,
      dark: brandColors.textInfo,
    },
    text: {
      primary: brandColors.textStrong,
      secondary: brandColors.textSubtle,
    },
    divider: brandColors.borderSubtlest,
    background: {
      default: brandColors.background02,
      paper: brandColors.background02,
    },
    navigation: {
      background: brandColors.backgroundPrimary,
      indicator: brandColors.textStrong,
      color: brandColors.textStrong,
      selectedColor: brandColors.textStrong,
      navItem: {
        hoverBackground: brandColors.background02,
      },
      submenu: {
        background: brandColors.background02,
      },
    },
  },
  defaultPageTheme: 'light',
  pageTheme: {
    ...defaultLightTheme?.getPageTheme,
    light: genPageTheme({
      colors: [brandColors.background02, '#6C7784'],
      shape: shapes.wave2,
      options: {
        fontColor: brandColors.textStrong,
      },
    }),
  },
});
